import React from 'react'
import Head from 'next/head'
import Pitch from '@src/components/DisposalPage/Pitch'
import FeatureList from '@src/components/DisposalPage/FeatureList'
import Approach from '@src/components/DisposalPage/Approach'
import CaseStudy from '@src/components/DisposalPage/CaseStudy'
import FAQ from '@src/components/DisposalPage/FAQ'
import SustainabilityDashboardPromo from '@src/components/DisposalPage/SustainabilityDashboardPromo'
import SchemaMarkup from '@src/components/SchemaMarkup'
import RelatedServices from '@src/components/RelatedServices'
import Clients from '@src/pages/LandingPage/Clients'
import TrustBox from '@src/pages/common/components/TrustBox'
import TrustedByClients from '@src/components/DisposalPage/TrustedByClients'
import pageClasses from '@src/styles/medical-waste-disposal-page.module.css'
// eslint-disable-next-line import/extensions
import medicalWaste from '../../json/medical-waste-disposal-data.json'

const medicalServiceData = {
  name: 'Medical and Laboratory Waste Disposal',
  description:
    'Business-only pickup and disposal of medical and laboratory waste and sharps, handled through Dimension’s hazardous-waste service with DOT- and RCRA-compliant transport and full documentation. Not available for residential customers.',
  serviceType: 'Medical and Laboratory Waste Disposal',
  url: 'https://getdimension.com/medical-waste-disposal',
  provider: {
    name: 'Dimension',
    url: 'https://getdimension.com',
    address: {
      streetAddress: '2261 Market Street, #4061',
      addressLocality: 'San Francisco',
      addressRegion: 'CA',
      postalCode: '94114',
      addressCountry: 'US',
    },
    telephone: '+1 (415) 340-7182',
  },
  offers: [
    { name: 'Medical and Lab Debris Pickup' },
    { name: 'Sharps Container Pickup' },
    { name: 'Expired and Unused Supply Disposal' },
    { name: 'Recurring Multi-Site Pickup' },
  ],
}

const medicalFAQData = {
  questions: medicalWaste.FAQContent.contentList.map((faq) => ({
    question: faq.heading.replace(/<[^>]*>/g, ''),
    answer: faq.textList.join(' '),
  })),
}

function MedicalWasteDisposal() {
  return (
    <>
      <Head>
        <title>Medical &amp; Laboratory Waste Disposal for Businesses | Dimension</title>
        <meta
          name="description"
          content="Business medical and laboratory waste disposal — pickup of medical/lab debris and sharps with DOT/RCRA-compliant transport and full documentation. Instant quote. Commercial customers only."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://getdimension.com/medical-waste-disposal" />
        <SchemaMarkup type="WebSite" />
        <SchemaMarkup type="Service" data={medicalServiceData} />
        <SchemaMarkup type="FAQPage" data={medicalFAQData} />
        <SchemaMarkup type="LocalBusiness" />
      </Head>
      <div className={pageClasses.root}>
        <Pitch
          variant="hazardous-waste"
          heading={medicalWaste.pitch.heading}
          material={medicalWaste.pitch.material}
          pitchText={medicalWaste.pitch.pitchText}
          ctaLink={medicalWaste.pitch.ctaLink}
          ctaText={medicalWaste.pitch.ctaText}
          belowCta={<TrustBox isMobile isDark={false} />}
          belowPitch={<TrustedByClients />}
        />
        <Clients />
        <FeatureList {...medicalWaste.solutions} />
        <SustainabilityDashboardPromo />
        <FeatureList {...medicalWaste.benefits} />
        <CaseStudy {...medicalWaste.caseStudy} />
        <Approach {...medicalWaste.approach} />
        <FAQ {...medicalWaste.FAQContent} />
        <RelatedServices
          services={[
            {
              label: 'Sharps Disposal',
              href: '/sharps-disposal',
              description:
                'Scheduled pickup of sealed sharps containers for medical facilities and other businesses.',
            },
            {
              label: 'Hazardous Waste Disposal',
              href: '/hazardous-waste-disposal',
              description:
                'Safe disposal of chemical waste, solvents, and other hazardous materials for businesses.',
            },
            {
              label: 'Lab Pack Services',
              href: '/lab-pack-services',
              description:
                'Chemist-led identification, segregation, and pack-out of small-container laboratory chemicals.',
            },
            {
              label: 'Regulated Waste Disposal',
              href: '/regulated-waste-disposal',
              description:
                'The catch-all for EPA- and DOT-regulated materials — universal waste, batteries, aerosols, gases, and more.',
            },
          ]}
        />
      </div>
    </>
  )
}

export default MedicalWasteDisposal
